(() => {
  'use strict';
  const ROOT_SELECTOR = '[data-ld-home-app-dialog-owner]';
  const instances = new WeakMap();

  class LDHomeAppCommunityDialog {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-ld-home-app-dialog]');
      this.frame = root.querySelector('[data-ld-home-app-dialog-frame]');
      this.closeButton = root.querySelector('[data-ld-home-app-dialog-close]');
      this.content = root.querySelector('[data-ld-community-inline-v3]');
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.state = 'CLOSED';
      this.lastFocus = null;
      this.homeState = null;
      this.homeScrollY = 0;
      this.homeSuspended = false;
      this.focusFrame = 0;
      this.restoreFrame = 0;
      this.autoOpened = false;
      this.designMode = Boolean(window.Shopify?.designMode);
    }

    connect() {
      if (!(this.dialog instanceof HTMLDialogElement)) return;
      document.addEventListener('ld:app-preview-open', (event) => this.onOpenRequest(event), { signal: this.signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (!this.designMode || !(event.target === this.root || event.target?.contains?.(this.root))) return;
        this.dialog.setAttribute('open','');
        this.root.dataset.dialogState = 'EDITOR_PREVIEW';
      }, { signal: this.signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (!this.designMode || !(event.target === this.root || event.target?.contains?.(this.root))) return;
        this.dialog.removeAttribute('open');
        this.root.dataset.dialogState = 'CLOSED';
      }, { signal: this.signal });
      this.closeButton?.addEventListener('click', () => this.close('button'), { signal: this.signal });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close('escape'); }, { signal: this.signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.close('backdrop');
      }, { signal: this.signal });
      this.dialog.addEventListener('close', () => this.afterNativeClose(), { signal: this.signal });
      const form = this.root.querySelector('.ld-community-inline-v3__form');
      form?.addEventListener('submit', () => {
        form.setAttribute('aria-busy', 'true');
        form.querySelector('button[type="submit"]')?.setAttribute('aria-disabled', 'true');
      }, { signal: this.signal });
      const feedback = this.root.querySelector('[data-ld-community-feedback]');
      if (feedback) this.focusFrame = requestAnimationFrame(() => {
        if (!this.signal.aborted && feedback.isConnected) feedback.focus({ preventScroll: false });
      });
    }

    isPublishable() {
      if (this.designMode) return true;
      return this.content?.dataset.publishStatus === 'ready';
    }

    onOpenRequest(event) {
      if (this.designMode) return;
      if (!this.isPublishable()) {
        this.root.dataset.dialogState = 'BLOCKED_INCOMPLETE';
        this.root.dataset.publishStatus = this.content?.dataset.publishStatus || 'unknown';
        return;
      }
      const source = String(event.detail?.source || 'home-finale');
      if (source === 'home-finale' && this.autoOpened) return;
      if (source === 'home-finale') this.autoOpened = true;
      this.open(event.detail?.trigger instanceof HTMLElement ? event.detail.trigger : document.activeElement, source);
    }

    open(trigger, source) {
      if (this.signal.aborted || !this.dialog || this.dialog.open || this.state === 'OPENING') return false;
      cancelAnimationFrame(this.restoreFrame);
      cancelAnimationFrame(this.focusFrame);
      this.state = 'OPENING';
      this.root.dataset.dialogState = this.state;
      this.lastFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      this.homeState = window.DV2Home?.getState?.() || null;
      this.homeScrollY = window.scrollY;
      this.homeSuspended = true;
      document.dispatchEvent(new CustomEvent('ld:home-suspend', { detail: { source: 'app-community-dialog' } }));
      try { this.dialog.showModal(); } catch (_error) { this.dialog.setAttribute('open', ''); }
      this.state = 'OPEN';
      this.root.dataset.dialogState = this.state;
      this.root.dataset.openSource = source;
      this.focusFrame = requestAnimationFrame(() => {
        if (!this.signal.aborted && this.dialog.open) this.closeButton?.focus({ preventScroll: true });
      });
      return true;
    }

    close(reason) {
      if (!this.dialog?.open || this.state === 'CLOSING') return false;
      this.state = 'CLOSING';
      this.root.dataset.dialogState = this.state;
      this.root.dataset.closeReason = reason;
      try { this.dialog.close(); } catch (_error) { this.dialog.removeAttribute('open'); this.afterNativeClose(); }
      return true;
    }

    releaseHome() {
      if (!this.homeSuspended) return;
      this.homeSuspended = false;
      document.dispatchEvent(new CustomEvent('ld:home-resume', { detail: { source: 'app-community-dialog' } }));
    }

    afterNativeClose() {
      if (this.state === 'CLOSED') return;
      this.state = 'CLOSED';
      this.root.dataset.dialogState = this.state;
      this.releaseHome();
      const record = this.homeState;
      const scrollY = this.homeScrollY;
      const returnFocus = this.lastFocus;
      this.restoreFrame = requestAnimationFrame(() => {
        if (this.signal.aborted || this.state !== 'CLOSED') return;
        if (record && window.DV2Home?.restore) window.DV2Home.restore(record);
        else window.scrollTo({ top: scrollY, behavior: 'auto' });
        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
      });
    }

    destroy() {
      this.abort.abort();
      cancelAnimationFrame(this.focusFrame);
      cancelAnimationFrame(this.restoreFrame);
      this.releaseHome();
      this.state = 'CLOSED';
      this.root.dataset.dialogState = this.state;
      if (this.dialog?.open) {
        try { this.dialog.close(); } catch (_error) { this.dialog.removeAttribute('open'); }
      }
    }
  }

  const scan = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new LDHomeAppCommunityDialog(root);
      instances.set(root, instance);
      instance.connect();
    });
  };
  const unload = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => { instances.get(root)?.destroy(); instances.delete(root); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
